import { useDispatch } from 'react-redux';
import '../../Common/css/app.css';
import '../../Common/css/main.css';
import '../../Common/css/common.css';
import '../../Common/css/payment.css';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { change_Current_Location, changeHeaderTitle } from '../../Common/store.js';
import axios from 'axios';
import { ChevronLeft, Check, ChevronRight, Clock, X, AlertTriangle, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoice, faCheck } from "@fortawesome/free-solid-svg-icons";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RECEIPT_CARD_SMS_NOTICE, RECEIPT_LABELS } from '../../Common/constants/receiptLabels';

dayjs.extend(utc);

// 0: 기존 email 전송, 1: 전표 보기 + 이메일 전송
// TEST 값만 바꾸면 env 재시작 없이 화면 확인 가능(빌드에는 null로 두세요).
const TEST_APP_RECEIPT_VIEW_MODE = 1; // 0 or 1
const APP_RECEIPT_VIEW_MODE =
  TEST_APP_RECEIPT_VIEW_MODE != null
    ? Number(TEST_APP_RECEIPT_VIEW_MODE)
    : Number(process.env.REACT_APP_APP_RECEIPT_VIEW_MODE ?? 0);

const RECEIPT_SMS_ENABLED = Number(process.env.REACT_APP_RECEIPT_SMS_ENABLED ?? 0) === 1;
const RECEIPT_KAKAO_ENABLED = Number(process.env.REACT_APP_RECEIPT_KAKAO_ENABLED ?? 0) === 1;

const APP_RECEIPT_SUPPLIER_MOCK = {
    supplierName: "엔토스페이먼츠",
    supplierOwnerName: "홍길동",
    supplierBusinessNo: "123-45-67890",
    supplierContact: "1661-0000",
    supplierAddress: "서울특별시 강남구 테헤란로 100",
};
const APP_RECEIPT_MERCHANT_MOCK = {
    merchantName: "유앤미케어",
    merchantOwnerName: "이영업",
    merchantBusinessNo: "987-65-43210",
    merchantContact: "02-1234-5678",
    merchantAddress: "서울특별시 서초구 반포대로 10",
};
const APP_RECEIPT_CARD_MERCHANT_MOCK = {
    cardMerchantName: "유앤미케어카드가맹점",
    cardMerchantOwnerName: "이영업",
    cardMerchantBusinessNo: "987-65-43210",
    cardMerchantContact: "-",
    cardMerchantAddress: "-",
};

// UTC → 한국시간(KST, UTC+9) 포맷
function formatKST(dateStr, format = "HH:mm") {
    if (!dateStr) return "-";
    return dayjs.utc(dateStr).add(9, "hour").format(format);
}

// API 응답 pg_raw_response에서 카드명 추출
function parseCardNameFromRaw(pgRawResponse) {
    if (!pgRawResponse || typeof pgRawResponse !== "string") return "-";
    try {
        const parsed = JSON.parse(pgRawResponse);
        const data = parsed?.data;
        return data?.cardNm || data?.cardKind || "-";
    } catch (_) {
        return "-";
    }
}

function mapPaymentToTransaction(item) {
  // payment_transaction_id를 기준으로 트랜잭션 ID 매핑
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    item.paymentId ??
    item.payment_id;
  const id = paymentTransactionId;
  // 승인일/생성일 후보 필드들
  const dateStr =
    item.approved_at ??
    item.approvedAt ??
    item.approval_date ??
    item.approvalDate ??
    item.created_at ??
    item.createdAt ??
    item.date;
    const date = dateStr ? dayjs.utc(dateStr).toISOString() : dayjs().toISOString();
  // 할부개월 필드 (cp manual / pg 공통 후보)
  const instmtMon =
    item.instmt_mon ??
    item.instmtMon ??
    item.pg_instmt_mon ??
    item.installmentMonths ??
    item.installment_months ??
    "00";
    const type = instmtMon === "00" ? "일시불" : `${instmtMon}개월`;
  const cardNoMasked =
    item.card_no_masked ??
    item.cardNoMasked ??
    item.maskedCardNo ??
    item.masked_card_no ??
    "-";
  const cardName =
    item.card_nm ??
    item.cardNm ??
    item.cardName ??
    parseCardNameFromRaw(item.pg_raw_response) ??
    "-";
    return {
        id,
    // 취소/전표에서 공통으로 사용할 paymentTransactionId를 명시적으로 보관
    paymentTransactionId,
    paymentId: paymentTransactionId,
        date,
    pgProvider: item.pg_provider ?? item.pgProvider ?? '',
    pharmacy:
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy ??
      item.customer_name ??
      item.pharmacy_name ??
      item.customerName ??
      item.pharmacyName ??
      "-",
    amount: Number(
      item.amount ??
        item.trdAmt ??
        item.trd_amt ??
        item.pg_trd_amt ??
        item.request_trd_amt ??
        0
    ),
        approvalNo: item.appr_no ?? item.apprNo ?? item.approvalNo ?? item.approval_no ?? item.approvalNumber ?? "",
        type,
        status: item.status ?? "",
        cancelStatus: item.status === "CANCELED" || item.canceled_at != null,
        canceledAt: item.canceled_at ?? item.canceledAt ?? null,
        cardName,
        cardNoMasked,
    };
}

function AppPaymentList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [searchInput, setSearchInput] = useState(""); // 입력창 값
    const [search, setSearch] = useState(""); // 검색 버튼으로 적용된 값(필터에 사용)
    const [searchHighlighted, setSearchHighlighted] = useState(false);
    const searchHistoryRef = useRef([]); // 검색 버튼 기준 히스토리
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canceledIds, setCanceledIds] = useState([]);
    const [toast, setToast] = useState({ visible: false, message: "" });
    const [cancelModal, setCancelModal] = useState({ open: false, txId: null, paymentId: null });
    const [receiptModal, setReceiptModal] = useState({ open: false, emailInput: "", paymentTransactionId: null });
    const [isSendingReceipt, setIsSendingReceipt] = useState(false);
    const [sendMethod, setSendMethod] = useState('email'); // 'email' | 'sms' | 'kakao'
    const [receiptPhone, setReceiptPhone] = useState('');
    const [receiptPreviewData, setReceiptPreviewData] = useState(null);
    const [receiptViewMode, setReceiptViewMode] = useState("approval");
    const [expandedPharmacies, setExpandedPharmacies] = useState(new Set());

    // 전표용 공급자/가맹점/PG가맹점 정보 (1회 조회)
    const [receiptSupplierInfo, setReceiptSupplierInfo] = useState(null);
    const [receiptMerchantInfo, setReceiptMerchantInfo] = useState(null);
    const [receiptPgMerchantRows, setReceiptPgMerchantRows] = useState([]);

    useEffect(() => {
        dispatch(change_Current_Location("거래조회"));
        dispatch(changeHeaderTitle({
            mainTitle: "거래내역",
            subTitle: ""
        }));
        // 전표 정보 1회 조회
        const baseUrl = process.env.REACT_APP_API || "";
        axios.get(baseUrl + "/api/terminal/receipt-info/supplier", { withCredentials: true })
            .then(({ data }) => {
                if (data?.code === 1000 && data?.data?.supplierInfo) {
                    setReceiptSupplierInfo(data.data.supplierInfo);
                }
            })
            .catch(() => {});
        axios.get(baseUrl + "/api/terminal/receipt-info/merchant", { withCredentials: true })
            .then(({ data }) => {
                if (data?.code === 1000 && data?.data?.merchantInfo) {
                    setReceiptMerchantInfo(data.data.merchantInfo);
                }
            })
            .catch(() => {});
        axios.get(baseUrl + "/api/terminal/receipt-info/pg-merchant", { withCredentials: true })
            .then(({ data }) => {
                if (data?.code === 1000 && data?.data?.rows) {
                    setReceiptPgMerchantRows(data.data.rows);
                }
            })
            .catch(() => {});
    }, [dispatch]);

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API || "";
        try {
            // GET /api/terminal/payment/manual
            // Query: page, limit, status, dateFrom, dateTo, q(약국명 검색)
            // dateFrom / dateTo 는 YYYY-MM-DD 형식으로 전달
            const params = {
                page: 1,
                limit: 100,
            };
            if (search.trim()) params.q = search.trim();

            const { data } = await axios.get(baseUrl + "/api/terminal/payment/manual", {
                params,
                withCredentials: true,
            });

            let rows = [];
            if (data?.code === 1000) {
                if (Array.isArray(data?.data?.rows)) {
                    rows = data.data.rows;
                } else if (Array.isArray(data?.data)) {
                    rows = data.data;
                }
            }

            if (Array.isArray(rows) && rows.length > 0) {
                const filteredRows = rows.filter((item) => {
                    const status = String(item.status || "").toUpperCase();
                    return status !== "FAILED"; // FAILED 만 목록에서 제외
                });
                setTransactions(filteredRows.map(mapPaymentToTransaction));
            } else {
                setTransactions([]);
            }
        } catch (err) {
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // 검색 적용 (검색 버튼 클릭 시에만 호출)
    const applySearch = (newApplied) => {
        if (newApplied === search) return;
        searchHistoryRef.current.push(search);
        setSearch(newApplied);
        setSearchInput(newApplied);
    };

    // API에서 q(약국명)로 필터링된 목록을 받아오므로 클라이언트 필터 없이 그대로 사용
    const filtered = transactions;

    // 년도/날짜 단위 그룹핑
    const groupedByYear = useMemo(() => {
        // 최신 거래가 위에 오도록 정렬
        const sorted = [...filtered].sort(
            (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
        );

        const yearMap = new Map();

        sorted.forEach(tx => {
            const d = dayjs.utc(tx.date).add(9, "hour"); // KST
            const year = d.year();
            const month = d.month() + 1;
            const day = d.date();
            const dateKey = d.format("YYYY-MM-DD");
            const dateLabel = `${month}/${day}`;

            if (!yearMap.has(year)) {
                yearMap.set(year, new Map());
            }
            const dateMap = yearMap.get(year);
            if (!dateMap.has(dateKey)) {
                dateMap.set(dateKey, { label: dateLabel, items: [] });
            }
            dateMap.get(dateKey).items.push(tx);
        });

        // Map → 배열 (년도/날짜 순서 유지: 최근 년도부터)
        return Array.from(yearMap.entries())
            .sort((a, b) => b[0] - a[0])
            .map(([year, dateMap]) => ({
                year,
                dates: Array.from(dateMap.entries())
                    .sort((a, b) => dayjs(b[0]).valueOf() - dayjs(a[0]).valueOf())
                    .map(([key, value]) => ({ key, ...value })),
            }));
    }, [filtered]);

    // 전표 전송 토스트
    const showToast = (message) => {
        setToast({ visible: true, message });
        // 일정 시간 후 자동 숨김
        setTimeout(() => {
            setToast((prev) => ({ ...prev, visible: false }));
        }, 2000);
    };

    const handleOpenReceiptModal = (tx) => {
        const paymentTransactionId =
            tx?.paymentTransactionId ??
            tx?.paymentId ??
            tx?.id ??
            null;
        if (APP_RECEIPT_VIEW_MODE === 1) {
            const amount = Number(tx?.amount || 0);
            const taxableAmount = Math.round(amount / 1.1);
            const vatAmount = amount - taxableAmount;
            const taxFreeAmount = 0;
            const serviceAmount = 0;
            const cardNumberMasked = tx?.cardNoMasked || "****-****-****-****";
            const isCanceled = !!tx?.cancelStatus;
            const approvalDateTime = formatKST(tx?.date, "YYYY.MM.DD. HH:mm:ss");
            const cancelDateTime = tx?.canceledAt ? formatKST(tx?.canceledAt, "YYYY.MM.DD. HH:mm:ss") : null;

            setReceiptPreviewData({
                paymentTransactionId,
                cardBrand: tx?.cardName || "-",
                cardNumberMasked,
                installmentMonth: tx?.type || "일시불",
                approvalNumber: tx?.approvalNo || "-",
                approvalDateTime,
                cancelDateTime,
                isCanceled,
                pharmacyName: tx?.pharmacy || "-",
                taxableAmount: taxableAmount.toLocaleString("ko-KR") + "원",
                vatAmount: vatAmount.toLocaleString("ko-KR") + "원",
                taxFreeAmount: taxFreeAmount.toLocaleString("ko-KR") + "원",
                serviceAmount: serviceAmount.toLocaleString("ko-KR") + "원",
                totalAmount: amount.toLocaleString("ko-KR") + "원",
                ...(receiptSupplierInfo ? {
                    supplierName: receiptSupplierInfo.name || APP_RECEIPT_SUPPLIER_MOCK.supplierName,
                    supplierOwnerName: receiptSupplierInfo.owner_name || APP_RECEIPT_SUPPLIER_MOCK.supplierOwnerName,
                    supplierBusinessNo: receiptSupplierInfo.business_no || APP_RECEIPT_SUPPLIER_MOCK.supplierBusinessNo,
                    supplierContact: receiptSupplierInfo.contact || APP_RECEIPT_SUPPLIER_MOCK.supplierContact,
                    supplierAddress: receiptSupplierInfo.address || APP_RECEIPT_SUPPLIER_MOCK.supplierAddress,
                } : APP_RECEIPT_SUPPLIER_MOCK),
                ...(receiptMerchantInfo ? {
                    merchantName: receiptMerchantInfo.name || APP_RECEIPT_MERCHANT_MOCK.merchantName,
                    merchantOwnerName: receiptMerchantInfo.boss_name || APP_RECEIPT_MERCHANT_MOCK.merchantOwnerName,
                    merchantBusinessNo: receiptMerchantInfo.business_no || APP_RECEIPT_MERCHANT_MOCK.merchantBusinessNo,
                    merchantContact: receiptMerchantInfo.phone || APP_RECEIPT_MERCHANT_MOCK.merchantContact,
                    merchantAddress: receiptMerchantInfo.address || APP_RECEIPT_MERCHANT_MOCK.merchantAddress,
                } : APP_RECEIPT_MERCHANT_MOCK),
                ...(() => {
                    const provider = String(tx?.pgProvider || '').toUpperCase();
                    const matched = receiptPgMerchantRows.find(
                        (row) => String(row?.pg_provider || '').toUpperCase() === provider,
                    );
                    if (matched) {
                        return {
                            cardMerchantName: matched.name || APP_RECEIPT_CARD_MERCHANT_MOCK.cardMerchantName,
                            cardMerchantOwnerName: matched.owner_name || APP_RECEIPT_CARD_MERCHANT_MOCK.cardMerchantOwnerName,
                            cardMerchantBusinessNo: matched.business_no || APP_RECEIPT_CARD_MERCHANT_MOCK.cardMerchantBusinessNo,
                            cardMerchantContact: matched.contact || matched.phone || APP_RECEIPT_CARD_MERCHANT_MOCK.cardMerchantContact,
                            cardMerchantAddress: matched.address || APP_RECEIPT_CARD_MERCHANT_MOCK.cardMerchantAddress,
                        };
                    }
                    return APP_RECEIPT_CARD_MERCHANT_MOCK;
                })(),
            });
            setReceiptViewMode("approval");
        }
        setReceiptModal({ open: true, emailInput: "", paymentTransactionId });
        setSendMethod('email');
        setReceiptPhone('');
    };

    const handleConfirmSendReceipt = async () => {
        if (receiptModal.paymentTransactionId == null) {
            showToast("전표 ID가 없습니다. 다시 시도해주세요.");
            return;
        }

        const closeReceipt = () => {
            setReceiptModal({ open: false, emailInput: "", paymentTransactionId: null });
            setReceiptPreviewData(null);
            setReceiptViewMode("approval");
            setSendMethod('email');
            setReceiptPhone('');
        };

        const baseUrl = process.env.REACT_APP_API || "";

        if (sendMethod === 'email') {
            const email = (receiptModal.emailInput || "").trim();
            if (!email) {
                showToast("전송할 이메일을 입력해주세요.");
                return;
            }
            try {
                setIsSendingReceipt(true);
                const { data } = await axios.post(
                    baseUrl + "/api/terminal/payment/manual/receipt-email",
                    {
                        paymentTransactionId: receiptModal.paymentTransactionId,
                        payment_transaction_id: receiptModal.paymentTransactionId,
                        email,
                    },
                    { withCredentials: true }
                );
                if (data?.code === 1000) {
                    showToast(data?.msg || "메일로 전표를 전송했습니다.");
                    closeReceipt();
                } else {
                    showToast(data?.msg || "전표 이메일 전송에 실패했습니다.");
                }
            } catch (err) {
                showToast(err?.response?.data?.msg || "전표 이메일 전송 중 오류가 발생했습니다.");
            } finally {
                setIsSendingReceipt(false);
            }
        } else if (sendMethod === 'sms') {
            const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
            if (!phone) {
                showToast("휴대폰 번호를 입력해주세요.");
                return;
            }
            try {
                setIsSendingReceipt(true);
                const { data } = await axios.post(
                    baseUrl + "/api/terminal/payment/manual/receipt-sms",
                    { paymentTransactionId: receiptModal.paymentTransactionId, phone },
                    { withCredentials: true }
                );
                if (data?.code === 1000) {
                    showToast(data?.msg || "전표 문자를 전송했습니다.");
                    closeReceipt();
                } else {
                    showToast("관리자에게 문의해주세요.");
                }
            } catch (err) {
                showToast("관리자에게 문의해주세요.");
            } finally {
                setIsSendingReceipt(false);
            }
        } else if (sendMethod === 'kakao') {
            const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
            if (!phone) {
                showToast("휴대폰 번호를 입력해주세요.");
                return;
            }
            try {
                setIsSendingReceipt(true);
                const { data } = await axios.post(
                    baseUrl + "/api/terminal/payment/manual/receipt-alimtalk",
                    { paymentTransactionId: receiptModal.paymentTransactionId, phone },
                    { withCredentials: true }
                );
                if (data?.code === 1000) {
                    showToast(data?.msg || "전표 알림톡을 발송했습니다.");
                    closeReceipt();
                } else {
                    showToast(data?.msg || "전표 알림톡 발송에 실패했습니다.");
                }
            } catch (err) {
                showToast(err?.response?.data?.msg || "전표 알림톡 발송 중 오류가 발생했습니다.");
            } finally {
                setIsSendingReceipt(false);
            }
        }
    };

    const handleCloseReceiptModal = () => {
        if (isSendingReceipt) return;
        setReceiptModal({ open: false, emailInput: "", paymentTransactionId: null });
        setReceiptPreviewData(null);
        setReceiptViewMode("approval");
        setSendMethod('email');
        setReceiptPhone('');
    };

    const handleOpenCancelModal = (tx) => {
        const txId = tx?.id ?? tx;
        // paymentTransactionId(원장 ID)를 우선적으로 사용
        const paymentTransactionId =
            tx?.paymentTransactionId ??
            tx?.paymentId ??
            tx?.id ??
            tx;
        setCancelModal({ open: true, txId, paymentId: paymentTransactionId });
    };

    const handleConfirmCancel = async () => {
        if (cancelModal.txId == null) return;
        const { txId, paymentId } = cancelModal; // paymentId = paymentTransactionId

        setCancelModal({ open: false, txId: null, paymentId: null });

        const baseUrl = process.env.REACT_APP_API || "";
        try {
            // 영업자 수기결제 취소: POST /api/terminal/payment/manual/cancel
            const { data } = await axios.post(
                baseUrl + "/api/terminal/payment/manual/cancel",
                {
                    paymentTransactionId: paymentId,
                    reason: "사용자 요청",
                },
                { withCredentials: true }
            );

            const status = data?.data?.status;
            const providerResult = data?.data?.provider_result || {};
            const success = providerResult?.success !== false && status === "COMPLETED";

            if (data?.code === 1000 && success) {
                showToast(data?.msg || "결제취소가 완료되었습니다.");
                // UI 상에서는 즉시 취소된 것처럼 표시
                setCanceledIds((prev) => (prev.includes(txId) ? prev : [...prev, txId]));
                fetchTransactions();
            } else {
                const msg =
                    data?.data?.outRsltMsg ??
                    data?.data?.provider_result?.outRsltMsg ??
                    data?.msg ??
                    "결제취소에 실패했습니다.";
                showToast(msg);
            }
        } catch (err) {
            const msg =
                err?.response?.data?.msg ||
                "결제취소에 실패했습니다.";
            showToast(msg);
        }
    };

    const handleCloseCancelModal = () => {
        setCancelModal({ open: false, txId: null, paymentId: null });
    };

    // 약국 클릭 핸들러 (펼침/접힘 토글)
    const togglePharmacyExpansion = (pharmacyName) => {
        setExpandedPharmacies((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(pharmacyName)) {
                newSet.delete(pharmacyName);
            } else {
                newSet.add(pharmacyName);
            }
            return newSet;
        });
    };

    // 약국별 거래내역 그룹화 (최대 5개)
    const pharmacyTransactions = useMemo(() => {
        const pharmacyMap = new Map();
        filtered.forEach(tx => {
            if (!pharmacyMap.has(tx.pharmacy)) {
                pharmacyMap.set(tx.pharmacy, []);
            }
            pharmacyMap.get(tx.pharmacy).push(tx);
        });

        // 각 약국별로 최신순 정렬 후 최대 5개만 반환
        const result = new Map();
        pharmacyMap.forEach((transactions, pharmacy) => {
            const sorted = [...transactions].sort(
                (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
            );
            result.set(pharmacy, sorted.slice(0, 5));
        });
        return result;
    }, [filtered]);

    return (
        <div
            style={{
                minHeight: "100dvh",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                maxWidth: "768px",
                margin: "0 auto",
                background: "#FFFFFF",
            }}
        >
            <style>
                {`
                    button, a, [onClick] {
                        outline: none !important;
                        -webkit-tap-highlight-color: transparent;
                        -webkit-touch-callout: none;
                        user-select: none;
                        transition: transform 0.1s ease, opacity 0.1s ease;
                    }
                    button:focus, a:focus, [onClick]:focus {
                        outline: none !important;
                    }
                    button:active, a:active, [onClick]:active {
                        outline: none !important;
                        transform: scale(0.96);
                        opacity: 0.8;
                    }
                    input:focus, select:focus, textarea:focus {
                        outline: none !important;
                        -webkit-tap-highlight-color: transparent;
                        border-color: #E5E7EB !important;
                        box-shadow: none !important;
                    }
                    input, select, textarea {
                        outline: none !important;
                    }
                    input:focus-visible {
                        outline: none !important;
                        border-color: #E5E7EB !important;
                        box-shadow: none !important;
                    }
                `}
            </style>
            {/* 고정 헤더 영역 */}
            <div
                style={{
                    flexShrink: 0,
                    background: "#FFFFFF",
                    zIndex: 10,
                    position: "relative",
                }}
            >
                {/* 헤더 */}
                <div style={{
                    background: "linear-gradient(160deg, #0b3a6f 0%, #0d2b53 60%, #08223f 100%)",
                    height: "62px",
                    padding: "0 12px 0 10px",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    boxSizing: "border-box"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0 2px",
                        position: "relative",
                        width: "100%",
                    }}>
                        <button
                            onClick={() => {
                                if (searchHistoryRef.current.length > 0) {
                                    const prev = searchHistoryRef.current.pop();
                                    setSearch(prev);
                                    setSearchInput(prev);
                                } else {
                                    navigate("/app/dashboard");
                                }
                            }}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: "2px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ChevronLeft size={28} color="#FFFFFF" />
                        </button>
                        <h1 style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "#FFFFFF",
                            margin: 0,
                        }}>
                            결제내역
                        </h1>
                        <div style={{ width: "28px", height: "28px" }} />
                    </div>
                </div>

                {/* 검색창 + 검색 버튼 */}
                <div
                    style={{
                        padding: "12px 16px 8px",
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                            <Search
                                size={18}
                                style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#9CA3AF",
                                }}
                            />
                            <input
                                type="text"
                                placeholder="업체명으로 검색하세요."
                                value={searchInput}
                                onChange={(e) => {
                                    setSearchInput(e.target.value);
                                    setSearchHighlighted(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        applySearch(searchInput.trim());
                                    }
                                }}
                                style={{
                                    width: "100%",
                                    padding: "10px 38px 10px 38px",
                                    border: "1px solid " + (searchHighlighted ? "#3182F6" : "#E5E7EB"),
                                    borderRadius: "12px",
                                    fontSize: "15px",
                                    background: searchHighlighted ? "#EFF6FF" : "#fff",
                                    outline: "none",
                                    transition: "all 0.3s ease",
                                    boxShadow: searchHighlighted ? "0 0 0 3px rgba(49, 130, 246, 0.1)" : "none",
                                    animation: searchHighlighted ? "searchPulse 1.5s ease-in-out" : "none",
                                }}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchInput("");
                                        if (search !== "") {
                                            searchHistoryRef.current.push(search);
                                            setSearch("");
                                        }
                                    }}
                                    style={{
                                        position: "absolute",
                                        right: "8px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "#E5E7EB",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "28px",
                                        height: "28px",
                                        borderRadius: "50%",
                                    }}
                                >
                                    <X size={16} color="#6B7280" />
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => applySearch(searchInput.trim())}
                            style={{
                                flexShrink: 0,
                                padding: "10px 14px",
                                borderRadius: "12px",
                                border: "1px solid #3182F6",
                                background: "#3182F6",
                                color: "#fff",
                                fontSize: "14px",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            검색
                        </button>
                    </div>
                </div>
            </div>

            {/* 스크롤 가능한 리스트 영역 - 상단 패딩 없음(스티키 날짜가 top에 꽉 붙도록) */}
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    minHeight: 0,
                    maxHeight: "calc(-130px + 100dvh)",
                    padding: "0 16px 16px",
                }}
            >
                {loading ? (
                    <div style={{ textAlign: "center", marginTop: "40px", color: "#9CA3AF", fontSize: "14px" }}>
                        로딩 중...
                    </div>
                ) : groupedByYear.length === 0 ? (
                    <div
                        style={{
                            textAlign: "center",
                            marginTop: "40px",
                            color: "#9CA3AF",
                            fontSize: "14px",
                        }}
                    >
                        결제내역이 없습니다.
                    </div>
                ) : (
                    <div style={{ paddingTop: "8px" }}>
                    {groupedByYear.map((yearGroup) => (
                        <div key={yearGroup.year} style={{ marginBottom: "20px" }}>
                            {/* 년도 */}
                            <div
                                style={{
                                    fontSize: "18px",
                                    fontWeight: 600,
                                    color: "#4B5563",
                                    // marginBottom: "8px",
                                }}
                            >
                                {yearGroup.year}
                            </div>

                            {/* 날짜별 그룹 - 날짜 헤더 스티키(해당 날짜 구간 스크롤 시 상단 고정) */}
                            {yearGroup.dates.map((dateGroup) => (
                                <div key={dateGroup.key} style={{ marginBottom: "2px" }}>
                                    {/* 스티키 날짜 헤더: 이 날짜 내역이 스크롤되는 동안 상단에 고정, 다음 날짜 나오면 위로 밀림 */}
                                    <div
                                        style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 10,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "4px",
                                            background: "#FFFFFF",
                                            paddingBottom: "6px",
                                            marginBottom: "2px",
                                            borderBottom: "1px solid #F3F4F6",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "50px",
                                                flexShrink: 0,
                                                fontSize: "16px",
                                                fontWeight: 700,
                                                color: "#111827",
                                                lineHeight: 1.2,
                                                paddingTop: "14px",
                                            }}
                                        >
                                            {dateGroup.label}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }} />
                                    </div>

                                    {/* 거래내역 리스트 (날짜 컬럼 맞춤으로 spacer) */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "4px",
                                        }}
                                    >
                                        <div style={{ width: "50px", flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                            {dateGroup.items.map((tx, index) => {
                                                const time = formatKST(tx.date, "HH:mm");
                                                const isLast = index === dateGroup.items.length - 1;
                                                const isCanceled = canceledIds.includes(tx.id) || tx.cancelStatus;

                                                // 상태에 따른 아이콘과 색상 결정
                                                let statusIcon = null;
                                                let statusBgColor = "";

                                                if (isCanceled) {
                                                    // 결제취소: 빨간색 동그라미 X
                                                    statusIcon = <X size={10} color="#FFFFFF" />;
                                                    statusBgColor = "#EF4444";
                                                } else {
                                                    // 결제완료: 초록색 동그라미 체크
                                                    statusIcon = <Check size={10} color="#FFFFFF" />;
                                                    statusBgColor = "#05c58a";
                                                }

                                                return (
                                                    <div
                                                        key={tx.id}
                                                        style={{
                                                            padding: "10px 4px",
                                                            borderBottom: isLast ? "none" : "1px solid #F3F4F6",
                                                            backgroundColor: isCanceled ? "#FEF2F2" : "transparent", // 취소요청중일 때는 배경색 변경 안함
                                                        }}
                                                    >
                                                        {/* 상단 행: 약국명(왼쪽), 금액(오른쪽) 동일 선상 */}
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                gap: "8px",
                                                            }}
                                                        >
                                                            <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
                                                                {/* <div
                                                                    style={{
                                                                        width: "16px",
                                                                        height: "16px",
                                                                        borderRadius: "50%",
                                                                        backgroundColor: statusBgColor,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    {statusIcon}
                                                                </div> */}
                                                                <button
                                                                    onClick={() => {
                                                                        applySearch(tx.pharmacy);
                                                                        setSearchHighlighted(true);
                                                                        setTimeout(() => setSearchHighlighted(false), 1500);
                                                                        showToast(`${tx.pharmacy}으로 검색되었습니다.`);
                                                                    }}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "4px",
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        padding: 0,
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize: "18px",
                                                                            fontWeight: 700,
                                                                            color: "#111827",
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                        }}
                                                                    >
                                                                        {tx.pharmacy}
                                                                    </span>
                                                                    <ChevronRight size={16} color="#9CA3AF" />
                                                                </button>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: "18px",
                                                                    fontWeight: 700,
                                                                    color: "#1D4ED8",
                                                                    textDecoration: isCanceled ? "line-through" : "none",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {tx.amount.toLocaleString("ko-KR")}원
                                                            </div>
                                                        </div>

                                                        {/* 중간 섹션: 시간(KST), 카드명, 승인번호 */}
                                                        <div
                                                            style={{
                                                                marginTop: "4px",
                                                                fontSize: "12px",
                                                                color: "#6B7280",
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            <div>
                                                                {time} | {tx.cardName ?? "-"} | {tx.approvalNo || "-"}
                                                            </div>
                                                        </div>

                                                        {/* 하단 행: 전표(왼쪽), 결제취소(오른쪽) */}
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                minHeight: "24px",
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => handleOpenReceiptModal(tx)}
                                                                style={{
                                                                    background: "transparent",
                                                                    border: "none",
                                                                    cursor: "pointer",
                                                                    padding: "4px 0",
                                                                    fontSize: "14px",
                                                                    fontWeight: 500,
                                                                    color: "#374151",
                                                                    textDecoration: "underline",
                                                                    textUnderlineOffset: "2px",
                                                                }}
                                                            >
                                                                {APP_RECEIPT_VIEW_MODE === 1 ? "전표 보기/전송" : "email 전표전송"}
                                                            </button>
                                                            {!isCanceled ? (
                                                                <button
                                                                    onClick={() => handleOpenCancelModal(tx)}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "3px",
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        cursor: "pointer",
                                                                        padding: "4px 0",
                                                                        color: "#ff5e5e",
                                                                        fontSize: "14px",
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            width: "14px",
                                                                            height: "14px",
                                                                            borderRadius: "50%",
                                                                            backgroundColor: "#ffa1a1",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                        }}
                                                                    >
                                                                        <X size={10} color="#6B7280" />
                                                                    </div>
                                                                    결제취소
                                                                </button>
                                                            ) : <span />}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                    </div>
                )}
            </div>

            {/* 애니메이션 스타일 */}
            <style>
                {`
                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            max-height: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            max-height: 500px;
                            transform: translateY(0);
                        }
                    }
                    @keyframes searchPulse {
                        0%, 100% {
                            box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
                        }
                        50% {
                            box-shadow: 0 0 0 6px rgba(49, 130, 246, 0.15);
                        }
                    }
                `}
            </style>

            {/* 토스트 알람 애니메이션 스타일 */}
            <style>
                {`
                    @keyframes slideDownToast {
                        from {
                            opacity: 0;
                            transform: translateX(-50%) translateY(-100%);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(-50%) translateY(0);
                        }
                    }
                `}
            </style>

            {/* 전표 전송 토스트 */}
            {toast.visible && (
                <div
                    style={{
                        position: "fixed",
                        top: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "100%",
                        maxWidth: "768px",
                        padding: "0 16px",
                        boxSizing: "border-box",
                        zIndex: 3000,
                        display: "flex",
                        justifyContent: "center",
                        animation: "slideDownToast 0.3s ease-out",
                        pointerEvents: "none", // 클릭 이벤트를 통과시켜 뒤로가기 버튼 클릭 가능하게
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "12px 16px",
                            borderRadius: "12px",
                            backgroundColor: "#FFFFFF",
                            color: "#111827",
                            fontSize: "14px",
                            fontWeight: 500,
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            pointerEvents: "auto", // 토스트 메시지 자체는 클릭 가능하게
                        }}
                    >
                        {/* 파란색 원형 아이콘 배경에 흰색 체크마크 */}
                        <div
                            style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: "#3182F6",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <Check size={14} color="#FFFFFF" />
                        </div>
                        <span>{toast.message}</span>
                    </div>
                </div>
            )}

            {/* 전표 모달 (env 분기: 0 기존 / 1 전표 보기+이메일 전송) */}
            {receiptModal.open && (
                APP_RECEIPT_VIEW_MODE === 1 && receiptPreviewData ? (
                    <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "768px", height: "100dvh", backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, overflowY: "auto", padding: "24px 0" }} onClick={handleCloseReceiptModal}>
                        <div style={{ backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e0e0e0", width: "330px", boxShadow: "0 10px 25px rgba(15,23,42,0.2)", display: "flex", flexDirection: "column", maxHeight: "80vh" }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ position: "sticky", top: 0, zIndex: 10, padding: "16px 20px", background: "#fff", borderBottom: "1px solid #333", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: "18px", fontWeight: 700, color: "#111", lineHeight: 1.2 }}>{RECEIPT_LABELS.titleKo}</div>
                                    <div style={{ marginTop: "2px", fontSize: "11px", fontWeight: 600, color: "#767676", lineHeight: 1.2 }}>{RECEIPT_LABELS.titleEn}</div>
                                </div>
                                {receiptPreviewData.isCanceled && (
                                    <div style={{ display: "flex", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                                        <button type="button" onClick={() => setReceiptViewMode("approval")} style={{ padding: "6px 12px", fontSize: "13px", fontWeight: 600, border: "none", background: receiptViewMode === "approval" ? "#0064ff" : "#fff", color: receiptViewMode === "approval" ? "#fff" : "#0064ff", cursor: "pointer" }}>승인</button>
                                        <button type="button" onClick={() => setReceiptViewMode("cancel")} style={{ padding: "6px 12px", fontSize: "13px", fontWeight: 600, border: "none", background: receiptViewMode === "cancel" ? "#b91c1c" : "#fff", color: receiptViewMode === "cancel" ? "#fff" : "#b91c1c", cursor: "pointer" }}>취소</button>
                                    </div>
                                )}
                            </div>
                            <div style={{ overflowY: "auto", flex: 1, padding: "20px" }}>
                                <div style={{ fontSize: "16px", fontWeight: 700, paddingBottom: "12px" }}>거래정보</div>
                                <div style={{ fontSize: "14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>카드종류</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.cardBrand}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>카드번호</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.cardNumberMasked}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>할부</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.installmentMonth}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>거래유형</span><span style={{ fontWeight: 500 }}>{receiptViewMode === "approval" ? "승인" : "취소"}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>승인번호</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.approvalNumber}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>{receiptViewMode === "approval" ? "거래일시" : "취소일시"}</span><span style={{ fontWeight: 500 }}>{receiptViewMode === "approval" ? receiptPreviewData.approvalDateTime : (receiptPreviewData.cancelDateTime ?? "-")}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>거래처명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.pharmacyName}</span></div>
                                </div>
                                <div style={{ borderBottom: "1px solid #e0e0e0", margin: "14px 0" }} />
                                <div style={{ fontSize: "16px", fontWeight: 700, paddingBottom: "12px" }}>공급대가</div>
                                <div style={{ fontSize: "14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>과세금액</span><span style={{ fontWeight: 700 }}>{receiptPreviewData.taxableAmount}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>부가세</span><span style={{ fontWeight: 700 }}>{receiptPreviewData.vatAmount}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>면세금액</span><span style={{ fontWeight: 700 }}>{receiptPreviewData.taxFreeAmount}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>봉사료</span><span style={{ fontWeight: 700 }}>{receiptPreviewData.serviceAmount}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}><span style={{ fontWeight: 700 }}>총 결제금액</span><span style={{ fontWeight: 700, color: "#f04438", fontSize: "16px" }}>{receiptPreviewData.totalAmount}</span></div>
                                </div>
                                <div style={{ borderBottom: "1px solid #e0e0e0", margin: "14px 0" }} />
                                <div style={{ fontSize: "16px", fontWeight: 700, paddingBottom: "12px" }}>{RECEIPT_LABELS.storeInfo}</div>
                                <div style={{ fontSize: "14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>상호명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.merchantName}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>대표자명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.merchantOwnerName}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>사업자등록번호</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.merchantBusinessNo}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>이용문의</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.merchantContact}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", gap: "12px" }}><span style={{ color: "#767676", flexShrink: 0 }}>사업장소재지</span><span style={{ fontWeight: 500, textAlign: "right" }}>{receiptPreviewData.merchantAddress}</span></div>
                                </div>
                                <div style={{ borderBottom: "1px solid #e0e0e0", margin: "14px 0" }} />
                                <div style={{ fontSize: "16px", fontWeight: 700, paddingBottom: "12px" }}>{RECEIPT_LABELS.pspInfo}</div>
                                <div style={{ fontSize: "14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>상호명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.supplierName}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>대표자명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.supplierOwnerName}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>사업자등록번호</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.supplierBusinessNo}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>이용문의</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.supplierContact}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", gap: "12px" }}><span style={{ color: "#767676", flexShrink: 0 }}>사업장소재지</span><span style={{ fontWeight: 500, textAlign: "right" }}>{receiptPreviewData.supplierAddress}</span></div>
                                </div>
                                <div style={{ borderBottom: "1px solid #e0e0e0", margin: "14px 0" }} />
                                <div style={{ fontSize: "16px", fontWeight: 700, paddingBottom: "12px" }}>{RECEIPT_LABELS.pgInfo}</div>
                                <div style={{ fontSize: "14px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>가맹점명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.cardMerchantName}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>대표자명</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.cardMerchantOwnerName}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>사업자등록번호</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.cardMerchantBusinessNo}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}><span style={{ color: "#767676" }}>이용문의</span><span style={{ fontWeight: 500 }}>{receiptPreviewData.cardMerchantContact}</span></div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", gap: "12px" }}><span style={{ color: "#767676", flexShrink: 0 }}>사업장소재지</span><span style={{ fontWeight: 500, textAlign: "right" }}>{receiptPreviewData.cardMerchantAddress}</span></div>
                                </div>
                                <div style={{ borderBottom: "1px solid #e0e0e0", margin: "14px 0" }} />
                                <div style={{ fontSize: "11px", color: "#999", lineHeight: 1.5 }}>
                                    <div>본 전표는 이메일 전송용으로 구성된 내역이며, 결제/정산 시스템의 원장과 차이가 있을 수 있습니다.</div>
                                    <div style={{ marginTop: "8px", color: "#666" }}>{RECEIPT_CARD_SMS_NOTICE}</div>
                                </div>
                            </div>
                            <div style={{ borderTop: "1px solid #eee", padding: "20px", background: "#fafafa" }}>
                                {/* 전송 방법 선택 */}
                                <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #e5e7eb", marginBottom: "12px" }}>
                                    <button type="button" onClick={() => setSendMethod('email')} style={{ flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: 600, border: "none", background: sendMethod === 'email' ? "#0064ff" : "#fff", color: sendMethod === 'email' ? "#fff" : "#4e5968", cursor: "pointer" }}>이메일</button>
                                    <button type="button" onClick={() => { if (RECEIPT_SMS_ENABLED) setSendMethod('sms'); }} style={{ flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: 600, border: "none", borderLeft: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", background: !RECEIPT_SMS_ENABLED ? "#fff" : sendMethod === 'sms' ? "#00b493" : "#fff", color: !RECEIPT_SMS_ENABLED ? "#adb5bd" : sendMethod === 'sms' ? "#fff" : "#4e5968", cursor: !RECEIPT_SMS_ENABLED ? "not-allowed" : "pointer" }}>{RECEIPT_SMS_ENABLED ? '문자' : '문자(준비중)'}</button>
                                    <button type="button" onClick={() => { if (RECEIPT_KAKAO_ENABLED) setSendMethod('kakao'); }} style={{ flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: 600, border: "none", background: !RECEIPT_KAKAO_ENABLED ? "#fff" : sendMethod === 'kakao' ? "#fee500" : "#fff", color: !RECEIPT_KAKAO_ENABLED ? "#adb5bd" : sendMethod === 'kakao' ? "#3c1e1e" : "#4e5968", cursor: !RECEIPT_KAKAO_ENABLED ? "not-allowed" : "pointer" }}>{RECEIPT_KAKAO_ENABLED ? '카카오톡' : '카카오톡(준비중)'}</button>
                                </div>
                                {sendMethod === 'email' && (
                                    <>
                                        <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4e5968", marginBottom: "6px" }}>이메일 주소</label>
                                        <input
                                            type="email"
                                            value={receiptModal.emailInput}
                                            onChange={(e) => setReceiptModal((prev) => ({ ...prev, emailInput: e.target.value }))}
                                            autoComplete="off"
                                            placeholder="example@email.com"
                                            onKeyDown={(e) => { if (e.key === "Enter" && !isSendingReceipt) handleConfirmSendReceipt(); }}
                                            style={{ width: "100%", marginBottom: "12px", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", outline: "none", boxSizing: "border-box" }}
                                        />
                                    </>
                                )}
                                {sendMethod === 'sms' && (
                                    <>
                                        <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4e5968", marginBottom: "6px" }}>휴대폰 번호</label>
                                        <input
                                            type="tel"
                                            value={receiptPhone}
                                            onChange={(e) => setReceiptPhone(e.target.value)}
                                            placeholder="01012345678"
                                            onKeyDown={(e) => { if (e.key === "Enter" && !isSendingReceipt) handleConfirmSendReceipt(); }}
                                            style={{ width: "100%", marginBottom: "12px", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", outline: "none", boxSizing: "border-box" }}
                                        />
                                    </>
                                )}
                                {sendMethod === 'kakao' && (
                                    <>
                                        <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4e5968", marginBottom: "6px" }}>휴대폰 번호</label>
                                        <input type="tel" value={receiptPhone} onChange={(e) => setReceiptPhone(e.target.value)} placeholder="01012345678" onKeyDown={(e) => { if (e.key === "Enter" && !isSendingReceipt) handleConfirmSendReceipt(); }} style={{ width: "100%", marginBottom: "12px", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", outline: "none", boxSizing: "border-box" }} />
                                    </>
                                )}
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <button type="button" onClick={handleCloseReceiptModal} disabled={isSendingReceipt} style={{ flex: 1, padding: "10px 12px", borderRadius: "8px", border: "none", background: "#e5e7eb", color: "#111827", fontSize: "14px", fontWeight: 600, cursor: isSendingReceipt ? "not-allowed" : "pointer" }}>닫기</button>
                                    <button type="button" onClick={handleConfirmSendReceipt} disabled={isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED)} style={{ flex: 1, padding: "10px 12px", borderRadius: "8px", border: "none", background: (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "#e5e7eb" : sendMethod === 'kakao' ? "#fee500" : isSendingReceipt ? "#93c5fd" : sendMethod === 'sms' ? "#00b493" : "#0064ff", color: (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "#9ca3af" : sendMethod === 'kakao' ? "#3c1e1e" : "#fff", fontSize: "14px", fontWeight: 600, cursor: isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "not-allowed" : "pointer" }}>
                                        {isSendingReceipt ? "전송중..." : (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "준비중" : sendMethod === 'kakao' ? "알림톡 전송" : sendMethod === 'sms' ? "문자 전송" : "이메일 전송"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "100%",
                            maxWidth: "768px",
                            height: "100dvh",
                            backgroundColor: "rgba(0,0,0,0.4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000,
                        }}
                        onClick={handleCloseReceiptModal}
                    >
                        <div
                            style={{
                                width: "85%",
                                maxWidth: "400px",
                                backgroundColor: "#FFFFFF",
                                borderRadius: "20px",
                                padding: "28px 24px 20px",
                                boxShadow: "0 10px 25px rgba(15,23,42,0.2)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ fontSize: "20px", fontWeight: 600, marginBottom: "16px", color: "#111827" }}>전표 전송</div>
                            {/* 전송 방법 선택 */}
                            <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #e5e7eb", marginBottom: "12px" }}>
                                <button type="button" onClick={() => setSendMethod('email')} style={{ flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: 600, border: "none", background: sendMethod === 'email' ? "#0064ff" : "#fff", color: sendMethod === 'email' ? "#fff" : "#4e5968", cursor: "pointer" }}>이메일</button>
                                <button type="button" onClick={() => setSendMethod('sms')} style={{ flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: 600, border: "none", borderLeft: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb", background: sendMethod === 'sms' ? "#00b493" : "#fff", color: sendMethod === 'sms' ? "#fff" : "#4e5968", cursor: "pointer" }}>문자</button>
                                <button type="button" onClick={() => setSendMethod('kakao')} style={{ flex: 1, padding: "8px 0", fontSize: "13px", fontWeight: 600, border: "none", background: sendMethod === 'kakao' ? "#fee500" : "#fff", color: sendMethod === 'kakao' ? "#3c1e1e" : "#4e5968", cursor: "pointer" }}>카카오톡</button>
                            </div>
                            {sendMethod === 'email' && (
                                <>
                                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4e5968", marginBottom: "6px" }}>이메일 주소</label>
                                    <input
                                        type="email"
                                        placeholder="example@email.com"
                                        value={receiptModal.emailInput}
                                        onChange={(e) => setReceiptModal((prev) => ({ ...prev, emailInput: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === "Enter" && !isSendingReceipt) handleConfirmSendReceipt(); }}
                                        style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", marginBottom: "16px", fontSize: "15px", border: "1px solid #E5E7EB", borderRadius: "12px", outline: "none" }}
                                    />
                                </>
                            )}
                            {sendMethod === 'sms' && (
                                <>
                                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4e5968", marginBottom: "6px" }}>휴대폰 번호</label>
                                    <input
                                        type="tel"
                                        placeholder="01012345678"
                                        value={receiptPhone}
                                        onChange={(e) => setReceiptPhone(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter" && !isSendingReceipt) handleConfirmSendReceipt(); }}
                                        style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", marginBottom: "16px", fontSize: "15px", border: "1px solid #E5E7EB", borderRadius: "12px", outline: "none" }}
                                    />
                                </>
                            )}
                            {sendMethod === 'kakao' && (
                                <>
                                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4e5968", marginBottom: "6px" }}>휴대폰 번호</label>
                                    <input type="tel" value={receiptPhone} onChange={(e) => setReceiptPhone(e.target.value)} placeholder="01012345678" onKeyDown={(e) => { if (e.key === "Enter" && !isSendingReceipt) handleConfirmSendReceipt(); }} style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", marginBottom: "16px", fontSize: "15px", border: "1px solid #E5E7EB", borderRadius: "12px", outline: "none" }} />
                                </>
                            )}
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    onClick={handleCloseReceiptModal}
                                    disabled={isSendingReceipt}
                                    style={{ flex: 1, padding: "10px 20px", borderRadius: "12px", border: "none", fontSize: "15px", fontWeight: 600, backgroundColor: "#E5E7EB", color: "#111827", cursor: isSendingReceipt ? "not-allowed" : "pointer" }}
                                >
                                    닫기
                                </button>
                                <button
                                    onClick={handleConfirmSendReceipt}
                                    disabled={isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED)}
                                    style={{ flex: 1, padding: "10px 20px", borderRadius: "12px", border: "none", fontSize: "15px", fontWeight: 600, backgroundColor: (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "#e5e7eb" : sendMethod === 'kakao' ? "#fee500" : isSendingReceipt ? "#93C5FD" : sendMethod === 'sms' ? "#00b493" : "#1D4ED8", color: (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "#9ca3af" : sendMethod === 'kakao' ? "#3c1e1e" : "#FFFFFF", cursor: isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "not-allowed" : "pointer" }}
                                >
                                    {isSendingReceipt ? "전송중..." : (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? "준비중" : sendMethod === 'kakao' ? "알림톡 전송" : sendMethod === 'sms' ? "문자 전송" : "이메일 전송"}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* 취소 확인 모달 */}
            {cancelModal.open && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "100%",
                        maxWidth: "768px",
                        height: "100dvh",
                        backgroundColor: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            width: "85%",
                            maxWidth: "400px",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "20px",
                            padding: "28px 24px 20px",
                            boxShadow: "0 10px 25px rgba(15,23,42,0.2)",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "18px",
                                fontWeight: 600,
                                marginBottom: "12px",
                                color: "#111827",
                            }}
                        >
                            취소요청을 하시겠습니까?
                        </div>
                        <div
                            style={{
                                fontSize: "15px",
                                color: "#6B7280",
                                marginBottom: "24px",
                                lineHeight: 1.5,
                            }}
                        >
                            선택한 거래를 취소 처리합니다.
                        </div>
                        <div
                            style={{
                                display: "flex",
                                gap: "8px",
                            }}
                        >
                            <button
                                onClick={handleCloseCancelModal}
                                style={{
                                    flex: 1,
                                    padding: "10px 20px",
                                    borderRadius: "12px",
                                    border: "none",
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    backgroundColor: "#E5E7EB",
                                    color: "#111827",
                                    cursor: "pointer",
                                }}
                            >
                                닫기
                            </button>
                            <button
                                onClick={handleConfirmCancel}
                                style={{
                                    flex: 1,
                                    padding: "10px 20px",
                                    borderRadius: "12px",
                                    border: "none",
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    backgroundColor: "#DC2626",
                                    color: "#FFFFFF",
                                    cursor: "pointer",
                                }}
                            >
                                취소하기
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <footer
                style={{
                    flexShrink: 0,
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#8b95a1',
                    borderTop: '1px solid #f2f4f6',
                }}
            >
                Copyright 2026 nToss-Pharm. All rights reserved.
            </footer>
        </div>
    );
}

export default AppPaymentList;
